import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { Info } from "lucide-react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import { utils, writeFile } from "xlsx";
import Button from "../../componentes/button/button";
import { CustomTable } from "../../componentes/table/CustomTable";
import FilialService from "../../services/resource/filialService";
import { STATUS_CONFRONTO, STATUS_MANUAL_CTE, statusInfo } from "./statusConfronto";

const filialService = new FilialService();

const formatarMoeda = (valor) => (valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatarData = (data) => data ? new Date(data + "T00:00:00").toLocaleDateString("pt-BR") : "-";

const TIPO_DOCUMENTO_INFO = {
    CTE: { label: "CTe", badge: "bg-sky-100 text-sky-800" },
    NFSE: { label: "NFS-e", badge: "bg-teal-100 text-teal-800" },
};

function tipoDocumentoInfo(tipo) {
    return TIPO_DOCUMENTO_INFO[tipo] || { label: tipo ?? "—", badge: "bg-muted text-muted-foreground" };
}

const KPI_DESCRICOES = {
    encontrados: "O documento existe no Fortes, foi localizado um único pagamento correspondente no SAP (mesmo número + série + filial) e o valor bate (diferença até R$ 0,01).",
    naoEncontrados: "Existe no Fortes, mas não foi localizado nenhum pagamento compensado no SAP para esse número + série + filial. Pode ainda não ter sido pago, ou o CTe pode ter sido substituído por outro — nesse caso, use o \"Status manual\" no detalhe do registro.",
    divergencias: "Foi encontrado um único pagamento no SAP correspondente, mas o valor é diferente do valor no Fortes (diferença maior que R$ 0,01).",
    cancelados: "O documento está cancelado no Fortes — não entra na cobrança de pendências.",
    ambiguos: "Foi encontrado mais de um pagamento no SAP que poderia corresponder a esse documento — geralmente porque o SAP não informou a série e existem várias séries com o mesmo número no Fortes. O sistema não escolhe sozinho; veja os candidatos no detalhe do registro.",
    ajustados: "Documentos com status informado manualmente pelo usuário (Substituído ou Ignorado), sobrepondo o cálculo automático.",
};

const colunas = [
    {
        key: "tipoDocumento", label: "Tipo", render: (v) => {
            const info = tipoDocumentoInfo(v);
            return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${info.badge}`}>{info.label}</span>;
        }
    },
    { key: "numeroCte", label: "Nº Documento" },
    { key: "numeroSerie", label: "Série", render: (v) => v ?? "—" },
    { key: "filialNome", label: "Filial" },
    { key: "fortesValorTotal", label: "Valor Fortes", render: (v) => formatarMoeda(v) },
    { key: "valorSap", label: "Valor SAP", render: (v) => v != null ? formatarMoeda(v) : "—" },
    { key: "diferencaValor", label: "Diferença", render: (v) => v != null ? formatarMoeda(v) : "—" },
    {
        key: "status", label: "Situação", render: (v) => {
            const info = statusInfo(v);
            return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${info.badge}`}>{info.label}</span>;
        }
    },
    { key: "fortesDataEmissao", label: "Dt. Emissão (Fortes)", format: "date" },
    { key: "fortesDataPrestacao", label: "Dt. Prestação (Fortes)", render: (v) => v ? formatarData(v) : "—" },
    { key: "dataDocumento", label: "Dt. Documento (SAP)", render: (v) => v ? formatarData(v) : "—" },
    { key: "dataCompensacao", label: "Dt. Compensação", render: (v) => v ? formatarData(v) : "—" },
    { key: "fortesChaveEletronica", label: "Chave Eletrônica", render: (v) => <ChaveEletronicaCell valor={v} /> }

];

function ConfrontoSapFortesPanel({ service, periodoPreset }) {
    const [dataEmissaoInicial, setDataEmissaoInicial] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
    const [dataEmissaoFinal, setDataEmissaoFinal] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
    const [dataCompensacaoEspecifica, setDataCompensacaoEspecifica] = useState(null);
    const [filialId, setFilialId] = useState("");
    const [status, setStatus] = useState("");
    const [tipoDocumento, setTipoDocumento] = useState("");
    const [numeroCte, setNumeroCte] = useState("");
    const [numeroSerie, setNumeroSerie] = useState("");
    const [codigoFornecedor, setCodigoFornecedor] = useState("");
    const [apenasDivergencias, setApenasDivergencias] = useState(false);
    const [apenasNaoEncontrados, setApenasNaoEncontrados] = useState(false);
    const [apenasCancelados, setApenasCancelados] = useState(false);

    const [filiais, setFiliais] = useState([]);
    const [relatorio, setRelatorio] = useState(null);
    const [loading, setLoading] = useState(false);
    const [registroSelecionado, setRegistroSelecionado] = useState(null);

    const [mostrarFormAjuste, setMostrarFormAjuste] = useState(false);
    const [ajusteForm, setAjusteForm] = useState(ajusteFormVazio());
    const [salvandoAjuste, setSalvandoAjuste] = useState(false);

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data || []));
    }, []);

    const buscar = useCallback(async (paramsOverride) => {
        setLoading(true);
        try {
            const res = await service.confronto({
                dataEmissaoInicial, dataEmissaoFinal, dataCompensacaoEspecifica,
                filialId, status, tipoDocumento, numeroCte, numeroSerie, codigoFornecedor,
                apenasDivergencias, apenasNaoEncontrados, apenasCancelados,
                ...paramsOverride,
            });
            setRelatorio(res.data);
        } finally {
            setLoading(false);
        }
    }, [service, dataEmissaoInicial, dataEmissaoFinal, dataCompensacaoEspecifica, filialId, status, tipoDocumento, numeroCte, numeroSerie, codigoFornecedor, apenasDivergencias, apenasNaoEncontrados, apenasCancelados]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        buscar();
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (periodoPreset?.dataEmissaoInicial && periodoPreset?.dataEmissaoFinal) {
            const preset = {
                dataEmissaoInicial: periodoPreset.dataEmissaoInicial,
                dataEmissaoFinal: periodoPreset.dataEmissaoFinal,
                dataCompensacaoEspecifica: periodoPreset.dataCompensacaoEspecifica ?? null,
            };
            setDataEmissaoInicial(preset.dataEmissaoInicial);
            setDataEmissaoFinal(preset.dataEmissaoFinal);
            setDataCompensacaoEspecifica(preset.dataCompensacaoEspecifica);
            buscar(preset);
        }
    }, [periodoPreset]);

    const abrirDetalhe = (row) => {
        setRegistroSelecionado(row);
        setMostrarFormAjuste(false);
        setAjusteForm(row.ajusteManual ? {
            status: row.ajusteManual.status,
            observacao: row.ajusteManual.observacao || "",
            cteNumeroSubstituto: row.ajusteManual.cteNumeroSubstituto || "",
            cteSerieSubstituto: row.ajusteManual.cteSerieSubstituto || "",
        } : ajusteFormVazio());
    };

    const fecharDetalhe = () => {
        setRegistroSelecionado(null);
        setMostrarFormAjuste(false);
    };

    const salvarAjuste = async () => {
        if (!ajusteForm.status) {
            toast.error("Selecione o status manual.");
            return;
        }
        setSalvandoAjuste(true);
        try {
            await service.salvarAjusteManualCte({
                filialId: registroSelecionado.filialId,
                numeroCte: registroSelecionado.numeroCte,
                numeroSerie: registroSelecionado.numeroSerie,
                status: ajusteForm.status,
                observacao: ajusteForm.observacao || null,
                cteNumeroSubstituto: ajusteForm.cteNumeroSubstituto || null,
                cteSerieSubstituto: ajusteForm.cteSerieSubstituto || null,
            });
            toast.success("Status manual salvo com sucesso!");
            fecharDetalhe();
            buscar();
        } finally {
            setSalvandoAjuste(false);
        }
    };

    const removerAjuste = async () => {
        setSalvandoAjuste(true);
        try {
            await service.removerAjusteManualCte(registroSelecionado.filialId, registroSelecionado.numeroCte, registroSelecionado.numeroSerie);
            toast.success("Ajuste manual removido.");
            fecharDetalhe();
            buscar();
        } finally {
            setSalvandoAjuste(false);
        }
    };

    const exportarExcel = () => {
        const registros = relatorio?.registros || [];
        if (registros.length === 0) {
            toast.info("Nenhum dado para exportar.");
            return;
        }

        const linhasFortes = registros.map(r => ({
            "Tipo": tipoDocumentoInfo(r.tipoDocumento).label,
            "Nº Documento": r.numeroCte,
            "Série": r.numeroSerie ?? "",
            "Filial": r.filialNome ?? "",
            "Dt. Emissão": r.fortesDataEmissao ? formatarData(r.fortesDataEmissao) : "",
            "Dt. Prestação": r.fortesDataPrestacao ? formatarData(r.fortesDataPrestacao) : "",
            "Cancelado": r.fortesCancelado ? "Sim" : "Não",
            "Valor Total": r.fortesValorTotal ?? 0,
            "Remetente": r.fortesRemetente ?? "",
            "Qtd. Mercadoria": r.fortesQtdMercadoria ?? "",
            "Chave Eletrônica": r.fortesChaveEletronica ?? "",
            "Situação": statusInfo(r.status).label,
        }));

        const linhasCompensadas = registros
            .filter(r => r.pagamentoCompensadaId)
            .map(r => ({
                "Nº Documento": r.numeroDocumento ?? "",
                "Referência": r.referencia ?? "",
                "CTE": r.numeroCte,
                "Série": r.numeroSerie ?? "",
                "Filial": r.filialNome ?? "",
                "Valor": r.valorSap ?? 0,
                "Dt. Documento": r.dataDocumento ? formatarData(r.dataDocumento) : "",
                "Dt. Pagamento": r.dataPagamento ? formatarData(r.dataPagamento) : "",
                "Dt. Compensação": r.dataCompensacao ? formatarData(r.dataCompensacao) : "",
                "Fornecedor": r.codigoFornecedor ?? "",
            }));

        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, utils.json_to_sheet(linhasFortes), "Fortes");
        utils.book_append_sheet(workbook, utils.json_to_sheet(linhasCompensadas), "Pagamento Compensadas");

        writeFile(workbook, `confronto-sap-fortes_${dataEmissaoInicial}_a_${dataEmissaoFinal}.xlsx`);
    };

    const resumo = relatorio?.resumo;

    return (
        <div className="rounded-xl border border-border bg-card p-5 shadow-card">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold text-foreground mb-1">Confronto SAP × Fortes</p>
                    <p className="text-xs text-muted-foreground mb-3">
                        Parte de todos os CTEs emitidos no Fortes no período e verifica se há pagamento compensado correspondente no SAP.
                    </p>
                </div>
                <Button
                    label="Exportar Excel"
                    iconLeft="fa fa-file-excel"
                    variant="white"
                    disabled={loading || (relatorio?.registros || []).length === 0}
                    onClick={exportarExcel}
                />
            </div>

            {dataCompensacaoEspecifica && (
                <div className="mb-3 inline-flex items-center gap-2 text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full">
                    Filtrado pela Data de Compensação: {formatarData(dataCompensacaoEspecifica)}
                    <button onClick={() => { setDataCompensacaoEspecifica(null); buscar({ dataCompensacaoEspecifica: null }); }} className="font-bold">×</button>
                </div>
            )}

            <div className="flex flex-wrap gap-3 items-end mb-4">
                <Campo label="Emissão inicial">
                    <input type="date" value={dataEmissaoInicial} onChange={e => setDataEmissaoInicial(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground" />
                </Campo>
                <Campo label="Emissão final">
                    <input type="date" value={dataEmissaoFinal} onChange={e => setDataEmissaoFinal(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground" />
                </Campo>
                <Campo label="Filial">
                    <select value={filialId} onChange={e => setFilialId(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-40">
                        <option value="">Todas</option>
                        {filiais.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </select>
                </Campo>
                <Campo label="Situação">
                    <select value={status} onChange={e => setStatus(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-44">
                        <option value="">Todas</option>
                        {STATUS_CONFRONTO.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </Campo>
                <Campo label="Tipo">
                    <select value={tipoDocumento} onChange={e => setTipoDocumento(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-32">
                        <option value="">Todos</option>
                        <option value="CTE">CTe</option>
                        <option value="NFSE">NFS-e</option>
                    </select>
                </Campo>
                <Campo label="CTE">
                    <input type="text" value={numeroCte} onChange={e => setNumeroCte(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-28" />
                </Campo>
                <Campo label="Série">
                    <input type="text" value={numeroSerie} onChange={e => setNumeroSerie(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-20" />
                </Campo>
                <Campo label="Fornecedor">
                    <input type="text" value={codigoFornecedor} onChange={e => setCodigoFornecedor(e.target.value)} className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-32" />
                </Campo>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div className="flex flex-wrap gap-4 text-xs text-foreground">
                    <Checkbox label="Apenas divergências" checked={apenasDivergencias} onChange={setApenasDivergencias} />
                    <Checkbox label="Apenas não encontrados" checked={apenasNaoEncontrados} onChange={setApenasNaoEncontrados} />
                    <Checkbox label="Apenas cancelados" checked={apenasCancelados} onChange={setApenasCancelados} />
                </div>
                <Button label="Filtrar" iconLeft="fas fa-filter" onClick={() => buscar()} loading={loading} />
            </div>

            {resumo && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                    <Kpi
                        label={`Total CTEs (${resumo.totalCtes})`}
                        valor={resumo.totalCtes - resumo.cancelados}
                        descricao="Quantidade de CTes e NFS-e emitidos no Fortes dentro do período e das filiais selecionados, sem contar os cancelados."
                    />
                    <Kpi
                        label="CTEs válidos no Fortes"
                        valor={resumo.ctesValidosFortes}
                        descricao="Documentos emitidos no Fortes que não estão cancelados — é o universo de documentos que precisam ter pagamento confirmado no SAP."
                    />
                    <Kpi
                        label="CTEs válidos no SAP"
                        valor={resumo.ctesValidosSap}
                        descricao="Documentos com uma correspondência única e confirmada no SAP (Encontrado ou Divergência de valor). Não inclui os Ambíguos, pois ali não há um único pagamento que se possa considerar válido com segurança."
                    />
                    <Kpi label="Encontrados" valor={resumo.encontrados} descricao={KPI_DESCRICOES.encontrados} />
                    <Kpi
                        label="Não encontrados"
                        valor={resumo.naoEncontrados}
                        destaque={resumo.naoEncontrados > 0}
                        descricao={KPI_DESCRICOES.naoEncontrados}
                    />
                    <Kpi
                        label="Divergências"
                        valor={resumo.divergencias}
                        destaque={resumo.divergencias > 0}
                        descricao={KPI_DESCRICOES.divergencias}
                    />
                    <Kpi label="Cancelados" valor={resumo.cancelados} descricao={KPI_DESCRICOES.cancelados} />
                    <Kpi
                        label="Ambíguos"
                        valor={resumo.ambiguos}
                        destaque={resumo.ambiguos > 0}
                        descricao={KPI_DESCRICOES.ambiguos}
                    />
                    <Kpi label="Ajustados manualmente" valor={resumo.ajustadosManualmente} descricao={KPI_DESCRICOES.ajustados} />
                </div>
            )}

            {resumo && (
                <div className="flex flex-wrap gap-6 mb-5 text-sm">
                    <span>Valor total Fortes: <strong>{formatarMoeda(resumo.valorTotalFortes)}</strong></span>
                    <span>Valor total SAP: <strong>{formatarMoeda(resumo.valorTotalSap)}</strong></span>
                    <span>Diferença total: <strong>{formatarMoeda(resumo.diferencaTotal)}</strong></span>
                </div>
            )}

            {resumo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <ResumoTipoBlock titulo="CTe" info={tipoDocumentoInfo("CTE")} resumo={resumo.resumoCte} />
                    <ResumoTipoBlock titulo="NFS-e" info={tipoDocumentoInfo("NFSE")} resumo={resumo.resumoNfse} />
                </div>
            )}

            <CustomTable
                data={relatorio?.registros || []}
                columns={colunas}
                loading={loading}
                pagination
                showHeader={false}
                className="text-[11px] border rounded-xl"
                pageSize={20}
                onRowSelect={abrirDetalhe}
            />

            <Dialog
                header="Detalhes do confronto"
                visible={!!registroSelecionado}
                onHide={fecharDetalhe}
                blockScroll
                className="w-[95%] md:w-[640px]"
            >
                {registroSelecionado && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                        <div>
                            <p className="font-semibold text-foreground mb-2">Fortes</p>
                            <DetalheLinha label="Tipo" valor={tipoDocumentoInfo(registroSelecionado.tipoDocumento).label} />
                            <DetalheLinha label="Nº Documento / Série" valor={`${registroSelecionado.numeroCte} / ${registroSelecionado.numeroSerie ?? "—"}`} />
                            <DetalheLinha label="Dt. Emissão" valor={formatarData(registroSelecionado.fortesDataEmissao)} />
                            {registroSelecionado.fortesDataPrestacao && (
                                <DetalheLinha label="Dt. Prestação" valor={formatarData(registroSelecionado.fortesDataPrestacao)} />
                            )}
                            <DetalheLinha label="Dt. Documento" valor={formatarData(registroSelecionado.fortesDataDocumento)} />
                            <DetalheLinha label="Valor total" valor={formatarMoeda(registroSelecionado.fortesValorTotal)} />
                            <DetalheLinha label="Remetente" valor={registroSelecionado.fortesRemetente} />
                            <DetalheLinha label="Qtd. mercadoria" valor={registroSelecionado.fortesQtdMercadoria} />
                            <DetalheLinha label="Cancelado" valor={registroSelecionado.fortesCancelado ? "Sim" : "Não"} />
                            <DetalheLinha label="Filial" valor={registroSelecionado.filialNome} />
                            <DetalheLinha label="Chave eletrônica" valor={registroSelecionado.fortesChaveEletronica} />
                        </div>
                        <div>
                            <p className="font-semibold text-foreground mb-2">SAP</p>
                            {registroSelecionado.status === "AMBIGUO" ? (
                                <div>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        Múltiplos pagamentos do SAP correspondem a este CTE — resolva manualmente:
                                    </p>
                                    {registroSelecionado.candidatosAmbiguosSap?.map((c, i) => (
                                        <div key={i} className="border border-border rounded-md p-2 mb-2 text-xs">
                                            <p>Nº Documento: {c.numeroDocumento}</p>
                                            <p>Referência: {c.referencia}</p>
                                            <p>Valor: {formatarMoeda(c.valor)}</p>
                                            <p>Dt. Compensação: {formatarData(c.dataCompensacao)}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : registroSelecionado.pagamentoCompensadaId ? (
                                <>
                                    <DetalheLinha label="Referência" valor={registroSelecionado.referencia} />
                                    <DetalheLinha label="Nº Documento" valor={registroSelecionado.numeroDocumento} />
                                    <DetalheLinha label="Dt. Documento" valor={formatarData(registroSelecionado.dataDocumento)} />
                                    <DetalheLinha label="Valor" valor={formatarMoeda(registroSelecionado.valorSap)} />
                                    <DetalheLinha label="Dt. Pagamento" valor={formatarData(registroSelecionado.dataPagamento)} />
                                    <DetalheLinha label="Dt. Compensação" valor={formatarData(registroSelecionado.dataCompensacao)} />
                                    <DetalheLinha label="Fornecedor" valor={registroSelecionado.codigoFornecedor} />
                                </>
                            ) : (
                                <p className="text-xs text-muted-foreground">Nenhum pagamento compensado encontrado no SAP para este CTE.</p>
                            )}
                        </div>
                    </div>
                )}

                {registroSelecionado && (
                    <div className="mt-5 pt-4 border-t border-border text-sm">
                        <p className="font-semibold text-foreground mb-2">Status manual</p>

                        {registroSelecionado.ajusteManual && !mostrarFormAjuste && (
                            <div className="border border-border rounded-md p-3 mb-3 text-xs bg-muted/40">
                                <p><strong>{statusInfo(registroSelecionado.status).label}</strong></p>
                                {registroSelecionado.ajusteManual.observacao && <p>Observação: {registroSelecionado.ajusteManual.observacao}</p>}
                                {registroSelecionado.ajusteManual.cteNumeroSubstituto && (
                                    <p>CTE substituto: {registroSelecionado.ajusteManual.cteNumeroSubstituto} / {registroSelecionado.ajusteManual.cteSerieSubstituto ?? "—"}</p>
                                )}
                                <p className="text-muted-foreground mt-1">
                                    Registrado por {registroSelecionado.ajusteManual.usuarioRegistro} em {formatarData(registroSelecionado.ajusteManual.dataHoraRegistro?.slice(0, 10))}
                                </p>
                            </div>
                        )}

                        {!mostrarFormAjuste ? (
                            <div className="flex gap-2">
                                <Button
                                    label={registroSelecionado.ajusteManual ? "Alterar status manual" : "Informar status manual"}
                                    variant="white"
                                    onClick={() => setMostrarFormAjuste(true)}
                                />
                                {registroSelecionado.ajusteManual && (
                                    <Button label="Remover ajuste" variant="red" loading={salvandoAjuste} onClick={removerAjuste} />
                                )}
                            </div>
                        ) : (
                            <div className="flex flex-col gap-3">
                                <p className="text-xs text-muted-foreground">
                                    Use isso quando o CTE for substituído por um novo (reemitido) e não for cancelado no Fortes,
                                    para que ele deixe de aparecer como pendente neste confronto.
                                </p>

                                <Campo label="Status manual">
                                    <select
                                        value={ajusteForm.status}
                                        onChange={e => setAjusteForm(f => ({ ...f, status: e.target.value }))}
                                        className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-64"
                                    >
                                        <option value="">Selecione...</option>
                                        {STATUS_MANUAL_CTE.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </select>
                                </Campo>

                                {ajusteForm.status === "SUBSTITUIDO" && (
                                    <div className="flex gap-3">
                                        <Campo label="Nº do CTE substituto">
                                            <input
                                                type="text"
                                                value={ajusteForm.cteNumeroSubstituto}
                                                onChange={e => setAjusteForm(f => ({ ...f, cteNumeroSubstituto: e.target.value }))}
                                                className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-32"
                                            />
                                        </Campo>
                                        <Campo label="Série do CTE substituto">
                                            <input
                                                type="text"
                                                value={ajusteForm.cteSerieSubstituto}
                                                onChange={e => setAjusteForm(f => ({ ...f, cteSerieSubstituto: e.target.value }))}
                                                className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-24"
                                            />
                                        </Campo>
                                    </div>
                                )}

                                <Campo label="Observação">
                                    <textarea
                                        value={ajusteForm.observacao}
                                        onChange={e => setAjusteForm(f => ({ ...f, observacao: e.target.value }))}
                                        rows={2}
                                        className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-full"
                                    />
                                </Campo>

                                <div className="flex justify-end gap-2">
                                    <Button label="Cancelar" variant="gray" onClick={() => setMostrarFormAjuste(false)} />
                                    <Button label="Salvar" variant="indigo" loading={salvandoAjuste} onClick={salvarAjuste} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
}

function ajusteFormVazio() {
    return { status: "", observacao: "", cteNumeroSubstituto: "", cteSerieSubstituto: "" };
}

function Campo({ label, children }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">{label}</label>
            {children}
        </div>
    );
}

function Checkbox({ label, checked, onChange }) {
    return (
        <label className="flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
            {label}
        </label>
    );
}

function Kpi({ label, valor, destaque, descricao }) {
    const [aberto, setAberto] = useState(false);

    return (
        <div className={`relative rounded-lg border p-3 ${destaque ? "border-destructive/40 bg-destructive/10" : "border-border bg-muted/40"}`}>
            <div className="flex items-start justify-between gap-1">
                <p className="text-lg font-bold text-foreground">{valor ?? 0}</p>
                {descricao && (
                    <button
                        type="button"
                        onClick={() => setAberto(a => !a)}
                        title="Mais informações"
                        className="text-muted-foreground hover:text-primary transition-colors shrink-0"
                    >
                        <Info size={14} />
                    </button>
                )}
            </div>
            <p className="text-xs text-muted-foreground">{label}</p>

            {aberto && (
                <div className="absolute z-20 top-full left-0 mt-1 w-56 rounded-md border border-border bg-card p-2.5 text-xs text-foreground shadow-lg">
                    {descricao}
                </div>
            )}
        </div>
    );
}

function ResumoTipoBlock({ titulo, info, resumo }) {
    if (!resumo) return null;

    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${info.badge}`}>{info.label}</span>
                <p className="text-sm font-semibold text-foreground">{titulo}</p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
                <Kpi label="Total" valor={resumo.total} descricao="Quantidade de documentos desse tipo emitidos no Fortes dentro do período e das filiais selecionados, sem contar os cancelados." />
                <Kpi label="Encontrados" valor={resumo.encontrados} descricao={KPI_DESCRICOES.encontrados} />
                <Kpi label="Não encontrados" valor={resumo.naoEncontrados} destaque={resumo.naoEncontrados > 0} descricao={KPI_DESCRICOES.naoEncontrados} />
                <Kpi label="Divergências" valor={resumo.divergencias} destaque={resumo.divergencias > 0} descricao={KPI_DESCRICOES.divergencias} />
                <Kpi label="Cancelados" valor={resumo.cancelados} descricao={KPI_DESCRICOES.cancelados} />
                <Kpi label="Ambíguos" valor={resumo.ambiguos} destaque={resumo.ambiguos > 0} descricao={KPI_DESCRICOES.ambiguos} />
                <Kpi label="Ajustados" valor={resumo.ajustadosManualmente} descricao={KPI_DESCRICOES.ajustados} />
            </div>

            <div className="flex flex-wrap gap-4 text-xs">
                <span>Valor Fortes: <strong>{formatarMoeda(resumo.valorTotalFortes)}</strong></span>
                <span>Valor SAP: <strong>{formatarMoeda(resumo.valorTotalSap)}</strong></span>
                <span>Diferença: <strong>{formatarMoeda(resumo.diferencaTotal)}</strong></span>
            </div>
        </div>
    );
}

function ChaveEletronicaCell({ valor }) {
    if (!valor) return "—";

    const copiar = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(valor);
            toast.success("Chave eletrônica copiada!");
        } catch {
            toast.error("Não foi possível copiar a chave eletrônica.");
        }
    };

    return (
        <span className="inline-flex items-center gap-1.5">
            <span>{valor}</span>
            <button
                type="button"
                onClick={copiar}
                title="Copiar chave eletrônica"
                className="text-muted-foreground hover:text-primary transition-colors"
            >
                <i className="fas fa-clipboard" />
            </button>
        </span>
    );
}

function DetalheLinha({ label, valor }) {
    return (
        <div className="flex justify-between border-b border-border/60 py-1">
            <span className="text-muted-foreground text-nowrap pr-2">{label}</span>
            <span className="text-foreground font-medium">{valor ?? "—"}</span>
        </div>
    );
}

export default ConfrontoSapFortesPanel;
